import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  User, Calendar, Gift, MessageSquare, ShieldAlert, MailOpen, CreditCard,
  Pencil, Send, Plus, Minus, X, KeyRound, ExternalLink, Download, Trash2, Coins,
} from 'lucide-react';
import { AdjustPointsDialog } from './AdjustPointsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '-'; }
};

const TAB_DEFS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'bookings', label: 'Bookings', icon: Calendar },
  { id: 'loyalty', label: 'Loyalty', icon: Gift },
  { id: 'communications', label: 'Communications', icon: MessageSquare },
  { id: 'allergies', label: 'Allergy Forms', icon: ShieldAlert },
  { id: 'invitations', label: 'Invitations', icon: MailOpen },
  { id: 'payments', label: 'Payments', icon: CreditCard },
];

const PaymentBadge = ({ status }) => {
  const styles = {
    fully_paid: 'bg-green-100 text-green-700',
    deposit_paid: 'bg-yellow-100 text-yellow-700',
    pending_payment: 'bg-gray-100 text-gray-600',
    cancelled_unpaid: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {(status || 'pending').replace(/_/g, ' ')}
    </span>
  );
};

export const CustomerProfileDialog = ({
  open, onOpenChange, customer, onRefresh,
  onOpenEditBooking, onOpenReminder, onOpenCancelBooking,
  onOpenMessageDialog, onOpenPayDialog, onOpenNewBooking,
  onOpenCreateInvitation,
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [busy, setBusy] = useState(false);

  // Editable profile mirror
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editChildName, setEditChildName] = useState('');
  const [editChildAge, setEditChildAge] = useState('');
  const [editMarketing, setEditMarketing] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [noteInput, setNoteInput] = useState('');

  useEffect(() => {
    if (!customer) return;
    const latest = customer.bookings?.[0] || {};
    setEditName(customer.name || '');
    setEditPhone(customer.phone || '');
    setEditAddress(customer.address || latest.address || '');
    setEditChildName(latest.child_name || '');
    setEditChildAge(latest.child_age != null ? String(latest.child_age) : '');
    setEditMarketing(!!customer.marketing_consent);
    setNoteInput(customer.notes || '');
    // Reset to profile tab when the customer changes
    setActiveTab('profile');
  }, [customer?.email]);  // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(() => onRefresh?.(customer?.email), [onRefresh, customer?.email]);

  // ────────── Computed (hooks MUST run before the early-return) ──────────
  const totalPaid = useMemo(
    () => (customer?.payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [customer?.payments],
  );

  // Admin-on-behalf redemption from the Loyalty tab.
  const [redeemTarget, setRedeemTarget] = useState(null);   // reward dict
  const [loyaltyAffordable, setLoyaltyAffordable] = useState([]);
  const [loyaltyAll, setLoyaltyAll] = useState([]);
  const [loyaltyFetched, setLoyaltyFetched] = useState(false);
  const [redeemBusy, setRedeemBusy] = useState(false);
  // Manual loyalty points adjustment (iter-100)
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustedBalance, setAdjustedBalance] = useState(null); // overrides customer.loyalty_balance after adjustment
  const [loyaltyHistoryRefreshKey, setLoyaltyHistoryRefreshKey] = useState(0);

  useEffect(() => {
    // Lazy-fetch rewards the first time the loyalty tab is shown
    if (activeTab !== 'loyalty' || !customer?.has_account || loyaltyFetched) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/loyalty/customer/${encodeURIComponent(customer.email)}`,
          { withCredentials: true });
        if (cancelled) return;
        setLoyaltyAffordable(r.data?.rewards || []);
        setLoyaltyAll(r.data?.all_rewards || []);
        setLoyaltyFetched(true);
      } catch {/* non-fatal */}
    })();
    return () => { cancelled = true; };
  }, [activeTab, customer?.has_account, customer?.email, loyaltyFetched]);

  if (!customer) return null;

  // ────────── Actions ──────────
  const saveProfile = async () => {
    if (!editName.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}`, {
        customer_name: editName.trim(),
        customer_phone: editPhone.trim(),
        address: editAddress.trim(),
        child_name: editChildName.trim(),
        child_age: editChildAge ? parseInt(editChildAge, 10) : undefined,
        sync_to_bookings: true,
      }, { withCredentials: true });
      toast.success('Profile saved');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}`,
        { notes: noteInput }, { withCredentials: true });
      toast.success('Notes saved');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    if (customer.tags?.includes(t)) { setTagInput(''); return; }
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}`,
        { tags: [...(customer.tags || []), t] }, { withCredentials: true });
      setTagInput('');
      reload();
    } catch { toast.error('Failed to add tag'); }
  };

  const removeTag = async (t) => {
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}`,
        { tags: (customer.tags || []).filter(x => x !== t) }, { withCredentials: true });
      reload();
    } catch { toast.error('Failed'); }
  };

  const sendPasswordReset = async () => {
    if (!window.confirm(`Email a password-reset link to ${customer.email}?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}/send-password-reset`,
        {}, { withCredentials: true });
      toast.success(`Reset link sent to ${customer.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reset link');
    } finally { setBusy(false); }
  };

  const redeemReward = async () => {
    if (!redeemTarget) return;
    setRedeemBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/loyalty/redeem-for-customer`, {
        email: customer.email, reward_id: redeemTarget.reward_id,
      }, { withCredentials: true });
      const v = res.data?.voucher;
      const msg = v?.discount_code
        ? `Redeemed — code: ${v.discount_code}`
        : `Redeemed — ${v?.reward_name}`;
      toast.success(msg);
      setRedeemTarget(null);
      setLoyaltyFetched(false);   // re-fetch affordable rewards
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redemption failed');
    } finally {
      setRedeemBusy(false);
    }
  };

  // ────────── Render helpers ──────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 bg-white"
        data-testid="customer-profile-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E8E5DF]">
          <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }} className="text-2xl">
            {customer.name || 'Customer Profile'}
          </DialogTitle>
          <div className="flex items-start justify-between gap-3 mt-2">
            <div className="text-sm text-[#6B6B7A] space-x-2">
              <span>{customer.email}</span>
              {customer.phone && <span>· {customer.phone}</span>}
              {customer.is_manual && (
                <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded uppercase font-semibold">
                  Manual
                </span>
              )}
              {customer.has_account && (
                <span className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded uppercase font-semibold">
                  Account
                </span>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm text-[#6B6B7A]">{customer.total_bookings} booking{customer.total_bookings === 1 ? '' : 's'}</p>
            </div>
          </div>
          {/* Quick actions row */}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="outline"
              onClick={() => onOpenNewBooking?.(customer)}
              className="rounded-full border-[#E8E5DF]" data-testid="qa-new-booking">
              <Plus className="w-3 h-3 mr-1" /> New Booking
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => onOpenMessageDialog?.(customer, 'email')}
              className="rounded-full border-[#E8E5DF]" data-testid="qa-send-email">
              <Send className="w-3 h-3 mr-1" /> Email
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => onOpenPayDialog?.(customer)}
              className="rounded-full border-[#E8E5DF]" data-testid="qa-take-payment">
              <CreditCard className="w-3 h-3 mr-1" /> Take Payment
            </Button>
            {customer.has_account && (
              <Button size="sm" variant="outline"
                onClick={sendPasswordReset} disabled={busy}
                className="rounded-full border-[#E8E5DF] text-[#FF3B6B]" data-testid="qa-password-reset">
                <KeyRound className="w-3 h-3 mr-1" /> Send Password Reset
              </Button>
            )}
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-[#FFFDF8] mx-6 mt-3 inline-flex w-fit gap-1 p-1 rounded-full" data-testid="profile-tabs">
            {TAB_DEFS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger
                key={id} value={id}
                className="rounded-full data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white px-3 py-1.5 text-xs"
                data-testid={`profile-tab-${id}`}
              >
                <Icon className="w-3.5 h-3.5 mr-1" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* PROFILE */}
            <TabsContent value="profile" className="space-y-4 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Name</Label>
                  <Input value={editName} onChange={e => setEditName(e.target.value)} className="mt-1" data-testid="pf-name" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Email</Label>
                  <Input value={customer.email} readOnly className="mt-1 bg-[#FFFDF8]" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Phone</Label>
                  <Input value={editPhone} onChange={e => setEditPhone(e.target.value)} className="mt-1" data-testid="pf-phone" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Address</Label>
                  <Input value={editAddress} onChange={e => setEditAddress(e.target.value)} className="mt-1" data-testid="pf-address" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Child's Name</Label>
                  <Input value={editChildName} onChange={e => setEditChildName(e.target.value)} className="mt-1" data-testid="pf-child-name" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Child's Age</Label>
                  <Input type="number" min={1} max={20} value={editChildAge} onChange={e => setEditChildAge(e.target.value)} className="mt-1" data-testid="pf-child-age" />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm pt-1">
                <Checkbox
                  checked={editMarketing}
                  onCheckedChange={(v) => setEditMarketing(!!v)}
                  data-testid="pf-marketing"
                  disabled
                />
                <span className="text-[#6B6B7A]">
                  Marketing consent ({customer.has_account ? 'managed by the customer in their portal' : 'no account yet'})
                </span>
              </label>

              <div>
                <Label className="text-xs text-[#6B6B7A]">Tags</Label>
                <div className="flex flex-wrap gap-2 mt-1 mb-2">
                  {(customer.tags || []).map(t => (
                    <Badge key={t} className="bg-[#FF3B6B]/10 text-[#FF3B6B] border-none flex items-center gap-1">
                      {t}
                      <button onClick={() => removeTag(t)} className="ml-1"><X className="w-3 h-3" /></button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput} onChange={e => setTagInput(e.target.value)}
                    placeholder="Add tag (e.g., VIP, Repeat)"
                    className="flex-1" onKeyDown={(e) => e.key === 'Enter' && addTag()}
                    data-testid="pf-tag-input"
                  />
                  <Button onClick={addTag} size="sm" className="rounded-full bg-[#FF3B6B] text-white" data-testid="pf-tag-add">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#6B6B7A]">Internal Notes</Label>
                <Textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3}
                  className="mt-1" placeholder="Add notes about this customer..." data-testid="pf-notes" />
                <Button onClick={saveNotes} size="sm" disabled={busy}
                  className="mt-2 bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="pf-save-notes">
                  Save Notes
                </Button>
              </div>

              <div className="flex justify-end pt-2 border-t border-[#E8E5DF]">
                <Button onClick={saveProfile} disabled={busy}
                  className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="pf-save-profile">
                  {busy ? 'Saving…' : 'Save Profile'}
                </Button>
              </div>
            </TabsContent>

            {/* BOOKINGS */}
            <TabsContent value="bookings" className="space-y-3 mt-0">
              {(customer.bookings || []).length === 0 ? (
                <div className="text-center text-sm text-[#6B6B7A] py-6">
                  No bookings yet.
                </div>
              ) : (customer.bookings || []).map(b => (
                <div key={b.booking_id}
                  className="p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]"
                  data-testid={`bk-row-${b.booking_id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1E1E24]">
                        {b.child_name}'s Party · {b.package_name}
                      </p>
                      <p className="text-xs text-[#6B6B7A]">
                        {b.date} · {b.time_slot} · {b.guest_count} guests
                      </p>
                      {b.booking_reference && (
                        <p className="text-[10px] text-[#9a9aa6] font-mono mt-0.5">{b.booking_reference}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {b.status === 'cancelled'
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">cancelled</span>
                        : <PaymentBadge status={b.payment_status} />}
                    </div>
                  </div>
                  {b.status !== 'cancelled' && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Button size="sm" variant="ghost"
                        onClick={() => onOpenEditBooking?.(b)}
                        className="h-7 text-xs text-[#6366F1]" data-testid={`bk-edit-${b.booking_id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => onOpenReminder?.(b.booking_id)}
                        className="h-7 text-xs text-[#06D6A0]" data-testid={`bk-remind-${b.booking_id}`}>
                        <MessageSquare className="w-3 h-3 mr-1" /> Remind
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => onOpenCancelBooking?.(b)}
                        className="h-7 text-xs text-red-500" data-testid={`bk-cancel-${b.booking_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            {/* LOYALTY */}
            <TabsContent value="loyalty" className="space-y-4 mt-0">
              <div className="rounded-2xl p-4 bg-gradient-to-r from-[#FF3B6B] to-[#FB923C] text-white flex items-start justify-between gap-3" data-testid="loyalty-summary">
                <div>
                  <p className="text-[11px] uppercase tracking-wide opacity-80">Current Balance</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Fredoka' }} data-testid="loyalty-balance-display">
                    {adjustedBalance ?? customer.loyalty_balance ?? 0} pts
                  </p>
                  {!customer.has_account && (
                    <p className="text-[11px] mt-1 opacity-90">
                      Customer needs to create an account before they can earn / redeem points.
                    </p>
                  )}
                </div>
                {customer.has_account && (
                  <Button
                    size="sm"
                    onClick={() => setAdjustOpen(true)}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full backdrop-blur border border-white/30 shrink-0"
                    data-testid="loyalty-adjust-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Adjust
                  </Button>
                )}
              </div>

              {/* Admin redeem on behalf */}
              {customer.has_account && (
                <div className="space-y-2 p-3 rounded-xl border border-[#FF3B6B]/30 bg-[#FFFDF8]" data-testid="loyalty-redeem-panel">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gift className="w-4 h-4 text-[#FF3B6B]" />
                    <p className="text-sm font-semibold text-[#1E1E24]">Redeem on Behalf</p>
                  </div>
                  {loyaltyAffordable.length === 0 && loyaltyAll.length > 0 ? (
                    <p className="text-[11px] text-[#6B6B7A]">Not enough points for any reward yet.</p>
                  ) : loyaltyAffordable.length === 0 ? (
                    <p className="text-[11px] text-[#6B6B7A]">Loading rewards…</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {loyaltyAffordable.map((r) => (
                        <button
                          key={r.reward_id}
                          onClick={() => setRedeemTarget(r)}
                          className="text-left px-3 py-2 rounded-lg border border-[#E8E5DF] bg-white hover:border-[#FF3B6B] hover:shadow-sm transition"
                          data-testid={`loyalty-reward-${r.reward_id}`}
                        >
                          <p className="text-xs font-semibold text-[#1E1E24] truncate">{r.name}</p>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-[10px] text-[#6B6B7A]">
                              {r.type === 'discount'
                                ? (r.discount_type === 'percent' ? `${r.discount_value}% off` : `$${r.discount_value} off`)
                                : r.type === 'freebie' ? 'Free add-on' : r.type}
                            </p>
                            <p className="text-[10px] font-bold text-[#FF3B6B]">{r.points_cost} pts</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[#6B6B7A] mt-1">
                    Customers can also redeem from their <b>My Loyalty</b> portal.
                  </p>
                </div>
              )}

              {(customer.loyalty_history || []).length === 0 ? (
                <p className="text-sm text-[#6B6B7A] text-center py-4">No loyalty activity yet.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-[#6B6B7A] font-semibold pt-1">
                    Points History ({(customer.loyalty_history || []).length})
                  </p>
                  {(customer.loyalty_history || []).map((row, i) => (
                    <div
                      key={row.ledger_id || i}
                      className="flex justify-between items-center p-3 bg-[#FFFDF8] rounded-xl text-sm border border-[#E8E5DF]"
                      data-testid={`loyalty-row-${row.ledger_id || i}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#1E1E24] truncate">
                          {row.description || row.event_type || 'Activity'}
                        </p>
                        <p className="text-[11px] text-[#6B6B7A] flex flex-wrap items-center gap-x-2 mt-0.5">
                          <span>{fmtDate(row.created_at)}</span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                              row.kind === 'earn'
                                ? 'bg-green-50 text-green-700'
                                : row.kind === 'redeem'
                                  ? 'bg-[#FFE9EE] text-[#FF3B6B]'
                                  : row.kind === 'refund'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {row.kind}
                          </span>
                          {row.booking_reference && (
                            <span className="font-mono text-[#1E1E24]">
                              {row.booking_reference}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className={`font-bold flex-shrink-0 ${row.points >= 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                        {row.points >= 0 ? `+${row.points}` : row.points}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* COMMUNICATIONS */}
            <TabsContent value="communications" className="space-y-3 mt-0">
              <div className="flex flex-wrap gap-2">
                <Button size="sm"
                  onClick={() => onOpenMessageDialog?.(customer, 'email')}
                  className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="com-send-email">
                  <Send className="w-3 h-3 mr-1" /> Send Email
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => onOpenMessageDialog?.(customer, 'sms')}
                  disabled={!customer.phone}
                  className="rounded-full" data-testid="com-send-sms">
                  <MessageSquare className="w-3 h-3 mr-1" /> Send SMS
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => onOpenReminder?.()}
                  className="rounded-full" data-testid="com-send-reminder">
                  Booking Reminder
                </Button>
              </div>
              {(customer.communications || []).length === 0 ? (
                <p className="text-sm text-[#6B6B7A] text-center py-6">No communications sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {(customer.communications || []).map((c, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#FFFDF8] rounded-xl text-sm border border-[#E8E5DF]" data-testid={`com-row-${i}`}>
                      <MailOpen className="w-4 h-4 text-[#6B6B7A] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1E1E24] truncate">{c.subject || c.kind}</p>
                        <p className="text-[11px] text-[#6B6B7A]">{fmtDate(c.sent_at)} · {c.status || 'sent'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ALLERGY FORMS */}
            <TabsContent value="allergies" className="space-y-3 mt-0">
              {(customer.allergy_forms || []).length === 0 ? (
                <p className="text-sm text-[#6B6B7A] text-center py-6">
                  No allergy forms submitted yet.
                </p>
              ) : (customer.allergy_forms || []).map(form => {
                const booking = (customer.bookings || []).find(b => b.booking_id === form.booking_id);
                const med = form.medical_data || {};
                const allergiesYes = med.has_allergies === 'yes' || med.allergies?.length > 0;
                return (
                  <div key={form.booking_id || form.form_id}
                    className="p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF]"
                    data-testid={`allergy-${form.booking_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-[#1E1E24]">
                          {booking ? `${booking.child_name}'s party · ${booking.date}` : (form.booking_id || 'Unknown booking')}
                        </p>
                        {allergiesYes ? (
                          <p className="text-xs text-[#FF3B6B] font-medium mt-1">
                            ⚠ Allergies / medical notes present
                          </p>
                        ) : (
                          <p className="text-xs text-[#06D6A0] mt-1">No allergies reported</p>
                        )}
                      </div>
                      <span className="text-[10px] text-[#6B6B7A]">
                        {fmtDate(form.submitted_at)}
                      </span>
                    </div>
                    {allergiesYes && (
                      <div className="text-xs text-[#6B6B7A] mt-2 space-y-1">
                        {med.allergies && <p><b>Allergies:</b> {Array.isArray(med.allergies) ? med.allergies.join(', ') : med.allergies}</p>}
                        {med.medical_other && <p><b>Other:</b> {med.medical_other}</p>}
                        {med.emergency_name && <p><b>Emergency:</b> {med.emergency_name} ({med.emergency_phone})</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </TabsContent>

            {/* INVITATIONS */}
            <TabsContent value="invitations" className="space-y-3 mt-0">
              <div className="flex flex-wrap gap-2">
                <Button size="sm"
                  onClick={() => onOpenCreateInvitation?.(customer)}
                  className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="inv-create">
                  <Plus className="w-3 h-3 mr-1" /> New Invitation
                </Button>
              </div>
              {(customer.invitations || []).length === 0 ? (
                <p className="text-sm text-[#6B6B7A] text-center py-6">
                  No invitations created for this customer yet.
                </p>
              ) : (customer.invitations || []).map(inv => (
                <a key={inv.invitation_id}
                  href={`/crm/invitations/${inv.invitation_id}`}
                  className="block p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] hover:border-[#FF3B6B] transition"
                  data-testid={`inv-row-${inv.invitation_id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-[#1E1E24]">{inv.title || inv.child_name + "'s Invitation"}</p>
                      <p className="text-xs text-[#6B6B7A]">{fmtDate(inv.created_at)} · {inv.recipients_count || 0} recipients</p>
                    </div>
                    <div className="text-right text-xs">
                      <p className="text-[#06D6A0] font-semibold">{inv.yes_count || 0} Yes</p>
                      <p className="text-[#6B6B7A]">{inv.no_count || 0} No</p>
                    </div>
                  </div>
                </a>
              ))}
            </TabsContent>

            {/* PAYMENTS */}
            <TabsContent value="payments" className="space-y-3 mt-0">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white rounded-xl border border-[#E8E5DF] p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Total Paid</p>
                  <p className="text-lg font-bold text-[#06D6A0]" style={{ fontFamily: 'Fredoka' }}>{fmt$(totalPaid)}</p>
                </div>
                <div className="bg-white rounded-xl border border-[#E8E5DF] p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Outstanding</p>
                  <p className="text-lg font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>{fmt$(customer.outstanding_balance)}</p>
                </div>
                <div className="bg-white rounded-xl border border-[#E8E5DF] p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">Transactions</p>
                  <p className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{(customer.payments || []).length}</p>
                </div>
              </div>
              {customer.outstanding_balance > 0 && (
                <Button size="sm"
                  onClick={() => onOpenPayDialog?.(customer)}
                  className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white" data-testid="pay-take-payment">
                  <CreditCard className="w-3 h-3 mr-1" /> Take Payment
                </Button>
              )}
              {(customer.payments || []).length === 0 ? (
                <p className="text-sm text-[#6B6B7A] text-center py-6">No payments yet.</p>
              ) : (
                <div className="space-y-2 mt-1">
                  {(customer.payments || []).map((p, i) => (
                    <div key={p.transaction_id || i} className="flex justify-between items-center p-3 bg-[#FFFDF8] rounded-xl text-sm border border-[#E8E5DF]" data-testid={`pay-row-${i}`}>
                      <div>
                        <p className="font-medium text-[#1E1E24]">{(p.payment_method || 'manual').toUpperCase()} · {p.payment_type || 'deposit'}</p>
                        <p className="text-[11px] text-[#6B6B7A]">
                          {fmtDate(p.created_at)}
                          {p.transaction_id && <span className="ml-2 font-mono">{p.transaction_id.slice(0, 10)}</span>}
                        </p>
                      </div>
                      <span className={`font-bold ${p.refund_status === 'refunded' ? 'text-[#FF3B6B] line-through' : 'text-[#06D6A0]'}`}>
                        {fmt$(p.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        {/* Redeem confirmation */}
        {redeemTarget && (
          <Dialog open={!!redeemTarget} onOpenChange={(o) => !o && setRedeemTarget(null)}>
            <DialogContent className="max-w-sm bg-white" data-testid="loyalty-redeem-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>
                  <Gift className="w-5 h-5" /> Redeem reward?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <p className="text-sm text-[#1E1E24] font-semibold">{redeemTarget.name}</p>
                <p className="text-xs text-[#6B6B7A]">
                  This will deduct <b className="text-[#FF3B6B]">{redeemTarget.points_cost} points</b> from {customer.name}'s balance and generate a
                  {redeemTarget.type === 'discount' ? ' single-use discount code' : ' freebie voucher'} they can use on their next booking.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRedeemTarget(null)} disabled={redeemBusy} className="rounded-full" data-testid="loyalty-redeem-cancel">
                  Cancel
                </Button>
                <Button onClick={redeemReward} disabled={redeemBusy}
                  className="rounded-full bg-[#FF3B6B] text-white hover:bg-[#E62E58]" data-testid="loyalty-redeem-confirm">
                  {redeemBusy ? 'Redeeming…' : 'Redeem'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>

      {/* Manual loyalty points adjustment (iter-100) */}
      <AdjustPointsDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        presetCustomer={customer && {
          user_id: customer.user_id,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          balance: customer.loyalty_balance || 0,
        }}
        onSuccess={({ balance }) => {
          // Update the displayed balance immediately, then refresh parent
          setAdjustedBalance(balance);
          setLoyaltyHistoryRefreshKey((k) => k + 1);
          reload();
        }}
      />
    </Dialog>
  );
};

export default CustomerProfileDialog;
